import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TgError,
  callTg,
  decisionScopeFields,
  hasDecisionScope,
  loadTelegramScript,
  readInitData,
} from './tgApi';
import type { TgChatMessage, TgDecision, TgDevStatus, TgItem, TgTeamAgent } from './tgApi';
import {
  agentName,
  ago,
  answerErrorText,
  answerLabel,
  canSend,
  chatSendErrorText,
  draftCount,
  MAX_DRAFT,
  recommendedKey,
  refetchAfter,
  trimDraft,
} from './tgModel';
import './tg.css';

/**
 * The Telegram Mini App page, "Kyu's AI Team" (design note r2, step C).
 * Outside RequireTap and TapLayout: the person opening it has no Hub session.
 * It proves itself with Telegram's signed initData and calls only tg-team.
 * Fetch on open and on pull-down, no polling.
 */

type Tab = 'today' | 'need' | 'log' | 'team';
type Load<T> = { status: 'loading' } | { status: 'error' } | { status: 'ok'; data: T };
interface Team {
  agents: TgTeamAgent[];
  status: TgDevStatus[];
}
interface Thread {
  ready: boolean;
  messages: TgChatMessage[];
}
interface Decisions {
  open: TgDecision[];
  answered: TgDecision[];
}

const LOADING = { status: 'loading' } as const;
const PULL_PX = 80;

function Rows() {
  return (
    <div aria-label="Loading">
      <div className="tg-skel" />
      <div className="tg-skel" />
      <div className="tg-skel" />
    </div>
  );
}

function Box({
  icon,
  title,
  text,
  retry,
}: {
  icon: string;
  title: string;
  text: string;
  retry?: () => void;
}) {
  return (
    <div className="tg-state">
      <div className="tg-state-icon">{icon}</div>
      <b>{title}</b>
      {text}
      {retry ? (
        <>
          <br />
          <button type="button" className="tg-retry" onClick={retry}>
            Try again
          </button>
        </>
      ) : null}
    </div>
  );
}

function ItemCard({ item, now }: { item: TgItem; now: number }) {
  return (
    <div className="tg-card">
      <div className="tg-row">
        <span className={`tg-dot tg-dot-${item.state}`} />
        <div className="tg-line">
          <span className="tg-agent">{agentName(item.agent)}</span>
          {item.priority === 'high' ? <span className="tg-pill tg-pill-high">high</span> : null}
          <span className="tg-pill">{item.state}</span>
          <div>{item.line}</div>
          <div className="tg-meta">{ago(item.created_at, now)}</div>
        </div>
      </div>
    </div>
  );
}

export function TgPage({
  loadTelegram = loadTelegramScript,
  refetchDelayMs = 1600,
}: {
  loadTelegram?: () => Promise<void>;
  refetchDelayMs?: number;
}) {
  const [tab, setTab] = useState<Tab>('today');
  const [expired, setExpired] = useState(false);
  const [today, setToday] = useState<Load<TgItem[]>>(LOADING);
  const [log, setLog] = useState<Load<TgItem[]>>(LOADING);
  const [decisions, setDecisions] = useState<Load<Decisions>>(LOADING);
  const [team, setTeam] = useState<Load<Team>>(LOADING);
  const [chat, setChat] = useState<string | null>(null);
  const [thread, setThread] = useState<Load<Thread>>(LOADING);
  const [draft, setDraft] = useState('');
  const [chatErr, setChatErr] = useState('');
  const [posting, setPosting] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const [now, setNow] = useState(() => Date.now());
  const [updated, setUpdated] = useState(false);
  const initData = useRef<string | null>(null);
  const pullFrom = useRef<number | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);

  const fetchOne = useCallback(
    async <T,>(
      action: string,
      pick: (body: Record<string, unknown>) => T,
      set: (v: Load<T>) => void,
    ) => {
      if (!initData.current) return;
      try {
        const body = await callTg(initData.current, { action });
        set({ status: 'ok', data: pick(body) });
        setNow(Date.now());
        setUpdated(true);
      } catch (e) {
        if (e instanceof TgError && e.code === 'expired') setExpired(true);
        else set({ status: 'error' });
      }
    },
    [],
  );

  const loadToday = useCallback(() => {
    setToday(LOADING);
    return fetchOne('get_today', (b) => (b.items ?? []) as TgItem[], setToday);
  }, [fetchOne]);
  const loadLog = useCallback(() => {
    setLog(LOADING);
    return fetchOne('get_log', (b) => (b.items ?? []) as TgItem[], setLog);
  }, [fetchOne]);
  const loadTeam = useCallback(() => {
    setTeam(LOADING);
    return fetchOne(
      'get_team',
      (b) => ({
        agents: (b.agents ?? []) as TgTeamAgent[],
        status: (b.status ?? []) as TgDevStatus[],
      }),
      setTeam,
    );
  }, [fetchOne]);
  const loadDecisions = useCallback(
    (quiet = false) => {
      if (!quiet) setDecisions(LOADING);
      return fetchOne(
        'get_decisions',
        (b) => ({
          open: (b.open ?? []) as TgDecision[],
          answered: (b.answered ?? []) as TgDecision[],
        }),
        setDecisions,
      );
    },
    [fetchOne],
  );

  useEffect(() => {
    let live = true;
    void loadTelegram().then(() => {
      if (!live) return;
      const tg = window.Telegram?.WebApp;
      tg?.ready?.();
      tg?.expand?.();
      const signed = readInitData();
      if (signed === '') {
        setExpired(true);
        return;
      }
      initData.current = signed;
      void loadToday();
      void loadDecisions();
    });
    return () => {
      live = false;
    };
  }, [loadTelegram, loadToday, loadDecisions]);

  const openLogOnce = useRef(false);
  useEffect(() => {
    if (tab === 'log' && !openLogOnce.current && initData.current) {
      openLogOnce.current = true;
      void loadLog();
    }
  }, [tab, loadLog]);

  const openTeamOnce = useRef(false);
  useEffect(() => {
    if (tab === 'team' && !openTeamOnce.current && initData.current) {
      openTeamOnce.current = true;
      void loadTeam();
    }
  }, [tab, loadTeam]);

  // Reading a thread marks it read on the server, so it runs only when a chat is opened.
  const chatNo = useRef(0); // which open of a chat is current; a late answer for an older open is dropped
  async function openChat(agent: string) {
    if (!initData.current) return;
    const mine = ++chatNo.current;
    setChat(agent);
    setDraft('');
    setChatErr('');
    setThread(LOADING);
    try {
      const reply = await callTg(initData.current, { action: 'get_thread', agent });
      if (mine !== chatNo.current) return;
      setThread({
        status: 'ok',
        data: {
          ready: reply.room_ready === true,
          messages: (reply.messages ?? []) as TgChatMessage[],
        },
      });
      setTeam((cur) =>
        cur.status !== 'ok'
          ? cur
          : {
              status: 'ok',
              data: {
                ...cur.data,
                agents: cur.data.agents.map((a) => (a.agent === agent ? { ...a, unread: 0 } : a)),
              },
            },
      );
    } catch (e) {
      if (mine !== chatNo.current) return;
      if (e instanceof TgError && e.code === 'expired') setExpired(true);
      else setThread({ status: 'error' });
    }
  }

  async function sendChat() {
    if (!initData.current || chat === null || posting || !canSend(draft)) return;
    setPosting(true);
    setChatErr('');
    const mine = chatNo.current;
    try {
      const reply = await callTg(initData.current, {
        action: 'post_to_agent',
        agent: chat,
        text: trimDraft(draft),
      });
      const message = reply.message as TgChatMessage;
      if (mine !== chatNo.current) return;
      setThread((cur) =>
        cur.status !== 'ok'
          ? cur
          : { status: 'ok', data: { ...cur.data, messages: [...cur.data.messages, message] } },
      );
      setDraft('');
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('success');
    } catch (e) {
      const message = chatSendErrorText(e instanceof TgError ? e.code : 'server');
      if (message === null) setExpired(true);
      else setChatErr(message);
    } finally {
      setPosting(false);
    }
  }

  const refresh = useCallback(() => {
    setUpdated(false);
    void loadToday();
    void loadDecisions();
    if (openLogOnce.current) void loadLog();
    if (openTeamOnce.current) void loadTeam();
  }, [loadToday, loadDecisions, loadLog, loadTeam]);

  async function answer(d: TgDecision, key: string) {
    if (!initData.current || sending) return;
    setSending(d.id);
    setRowErr({});
    try {
      const result = await callTg(initData.current, {
        action: 'answer_decision',
        id: d.id,
        option_key: key,
        ...(d.decision_version === undefined ? {} : { decision_version: d.decision_version }),
      });
      if (result.state === 'held') {
        await loadDecisions();
        return;
      }
      setDecisions((cur) =>
        cur.status !== 'ok'
          ? cur
          : {
              status: 'ok',
              data: {
                open: cur.data.open.filter((x) => x.id !== d.id),
                answered: [
                  {
                    ...d,
                    answer: key,
                    answered_at: new Date().toISOString(),
                    answered_via: 'miniapp',
                  },
                  ...cur.data.answered,
                ],
              },
            },
      );
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('success');
    } catch (e) {
      const code = e instanceof TgError ? e.code : 'server';
      const text = answerErrorText(code);
      if (text === null) setExpired(true);
      else {
        setRowErr({ [d.id]: text });
        if (refetchAfter(code))
          window.setTimeout(
            () => void loadDecisions(true).then(() => setRowErr({})),
            refetchDelayMs,
          );
      }
    } finally {
      setSending(null);
    }
  }

  const open = decisions.status === 'ok' ? decisions.data.open : [];
  const unreadTotal =
    team.status === 'ok' ? team.data.agents.reduce((sum, a) => sum + a.unread, 0) : 0;

  function teamView() {
    if (team.status === 'loading') return <Rows />;
    if (team.status === 'error')
      return (
        <Box
          icon="📶"
          title="Can't reach the team"
          text="Check your connection."
          retry={() => void loadTeam()}
        />
      );
    const { agents, status } = team.data;
    if (agents.length === 0 && status.length === 0)
      return (
        <Box
          icon="💬"
          title="No chats yet"
          text="Your agents appear here once their chat is set up."
        />
      );
    return (
      <>
        <div className="tg-section-title">Chat with</div>
        {agents.map((a) => (
          <button
            type="button"
            key={a.agent}
            className="tg-tm"
            onClick={() => void openChat(a.agent)}
          >
            <span className="tg-av">{agentName(a.agent).charAt(0)}</span>
            <span className="tg-tm-body">
              <span className="tg-tm-top">
                <b>{agentName(a.agent)}</b>
                {a.last ? <span className="tg-meta">{ago(a.last.at, now)}</span> : null}
              </span>
              <span className="tg-prev">
                {!a.room_ready
                  ? 'Chat not set up yet'
                  : a.last
                    ? `${a.last.mine ? 'You: ' : ''}${a.last.text.split('\n')[0]}`
                    : 'No messages yet'}
              </span>
            </span>
            {a.unread > 0 ? <span className="tg-unread">{a.unread}</span> : null}
          </button>
        ))}
        <div className="tg-section-title">Developers (status only, no chat)</div>
        {status.map((d) => (
          <div className="tg-tm tg-tm-static" key={d.agent}>
            <span className="tg-av tg-av-dev">{agentName(d.agent).slice(-1)}</span>
            <span className="tg-tm-body">
              <span className="tg-tm-top">
                <b>{agentName(d.agent)}</b>
                <span className="tg-pill">{d.state}</span>
              </span>
              <span className="tg-prev">{d.line}</span>
            </span>
          </div>
        ))}
      </>
    );
  }

  function chatView() {
    const name = agentName(chat ?? '');
    const count = draftCount(draft);
    const ready = thread.status === 'ok' && thread.data.ready;
    let content;
    if (thread.status === 'loading') content = <Rows />;
    else if (thread.status === 'error')
      content = (
        <Box
          icon="📶"
          title="Can't load this chat"
          text="Check your connection."
          retry={() => void openChat(chat ?? '')}
        />
      );
    else if (!thread.data.ready)
      content = (
        <Box
          icon="🔒"
          title={`${name}'s chat is not set up yet`}
          text="It is switched on by an approved step. Nothing you type is sent anywhere."
        />
      );
    else if (thread.data.messages.length === 0)
      content = <Box icon="💬" title="No messages yet" text={`Say hello to ${name}.`} />;
    else
      content = (
        <>
          <div className="tg-older">Showing the last 30 messages</div>
          {thread.data.messages.map((m) => (
            <div key={m.id} className={`tg-msg${m.mine ? ' tg-msg-me' : ''}`}>
              {m.text}
              <span className="tg-msg-time">{ago(m.at, now)}</span>
            </div>
          ))}
        </>
      );
    return (
      <div className="tg-chat">
        <div className="tg-chat-bar">
          <button
            type="button"
            className="tg-back"
            aria-label="Back to Team"
            onClick={() => setChat(null)}
          >
            ‹ Team
          </button>
          <span className="tg-av">{name.charAt(0)}</span>
          <b>{name}</b>
        </div>
        <div className="tg-msgs">{content}</div>
        {ready ? (
          <div className="tg-composer">
            {chatErr ? <div className="tg-err">{chatErr}</div> : null}
            <div className="tg-composer-in">
              <textarea
                rows={1}
                placeholder={`Message ${name}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button
                type="button"
                className="tg-send"
                disabled={posting || !canSend(draft)}
                onClick={() => void sendChat()}
              >
                Send
              </button>
            </div>
            <div className={`tg-cnt${count > MAX_DRAFT ? ' tg-cnt-over' : ''}`}>
              <span>Plain text only. No files.</span>
              <span>{`${count}/${MAX_DRAFT}`}</span>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  function body() {
    if (expired)
      return (
        <Box
          icon="🔒"
          title="Open this from Telegram again"
          text="The session ran out. Close the app and tap the menu button."
        />
      );
    if (chat !== null) return chatView();
    if (tab === 'team') return teamView();
    if (tab === 'today') {
      if (today.status === 'loading') return <Rows />;
      if (today.status === 'error')
        return (
          <Box
            icon="📶"
            title="Can't reach the team"
            text="Check your connection."
            retry={() => void loadToday()}
          />
        );
      return (
        <>
          {open.length > 0 ? (
            <div className="tg-banner">
              <div>
                <b>{`${open.length} need${open.length === 1 ? 's' : ''} you`}</b>
                <div className="tg-meta">Decisions waiting</div>
              </div>
              <button type="button" className="tg-retry" onClick={() => setTab('need')}>
                Open
              </button>
            </div>
          ) : null}
          <div className="tg-section-title">Last 36 hours</div>
          {today.data.length === 0 ? (
            <Box
              icon="☀"
              title="Quiet day"
              text="Nothing from the team yet. New updates appear here."
            />
          ) : (
            today.data.map((i) => <ItemCard key={i.id} item={i} now={now} />)
          )}
        </>
      );
    }
    if (tab === 'log') {
      if (log.status === 'loading') return <Rows />;
      if (log.status === 'error')
        return (
          <Box
            icon="📶"
            title="Can't reach the team"
            text="Check your connection."
            retry={() => void loadLog()}
          />
        );
      return (
        <>
          <div className="tg-section-title">Last 7 days</div>
          {log.data.length === 0 ? (
            <Box
              icon="📜"
              title="Nothing logged yet"
              text="Finished work from the last 7 days shows here."
            />
          ) : (
            <>
              {log.data.map((i) => (
                <ItemCard key={i.id} item={i} now={now} />
              ))}
              <div className="tg-meta tg-foot">Showing the latest 50</div>
            </>
          )}
        </>
      );
    }
    if (decisions.status === 'loading') return <Rows />;
    if (decisions.status === 'error')
      return (
        <Box
          icon="📶"
          title="Can't reach the team"
          text="Check your connection."
          retry={() => void loadDecisions()}
        />
      );
    const { answered } = decisions.data;
    return (
      <>
        {open.length === 0 ? (
          <Box icon="✅" title="You're all caught up" text="Nothing needs you right now." />
        ) : null}
        {open.map((d) => {
          const rec = recommendedKey(d.recommendation, d.options);
          const managed = d.decision_version !== undefined;
          const complete = hasDecisionScope(d);
          const stale = managed && d.decision_state === 'stale';
          const needsTide =
            managed &&
            (stale ||
              !complete ||
              d.options.some((o) => d.decision_details?.option_actions[o.key] === 'ask'));
          return (
            <div className="tg-card" key={d.id}>
              <div className="tg-q">{d.question}</div>
              {d.decision_state === 'held' ? <div className="tg-meta">Held</div> : null}
              {stale ? (
                <div className="tg-meta">This request changed. Review it in Tide.</div>
              ) : null}
              {managed && complete && !stale ? (
                <dl>
                  {decisionScopeFields.map((field) => (
                    <div key={field}>
                      <dt className="tg-meta">{field.charAt(0).toUpperCase() + field.slice(1)}</dt>
                      <dd>{d.decision_details![field]}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {d.recommendation ? (
                <div className="tg-rec">
                  <span>Recommended:</span> {d.recommendation}
                </div>
              ) : null}
              <div className="tg-btns">
                {d.options
                  .filter(
                    (o) =>
                      !managed ||
                      (!stale && complete && d.decision_details?.option_actions[o.key] !== 'ask'),
                  )
                  .map((o) => (
                    <button
                      type="button"
                      key={o.key}
                      className={`tg-btn${o.key === rec ? ' tg-btn-rec' : ''}`}
                      disabled={sending !== null}
                      onClick={() => void answer(d, o.key)}
                    >
                      {o.label}
                    </button>
                  ))}
              </div>
              {needsTide ? <a href="/tide">Open Tide to review or ask about this request</a> : null}
              {sending === d.id ? <div className="tg-meta">Sending...</div> : null}
              {rowErr[d.id] ? <div className="tg-err">{rowErr[d.id]}</div> : null}
              {d.default_if_skipped ? <div className="tg-skip">{d.default_if_skipped}</div> : null}
            </div>
          );
        })}
        {answered.length > 0 ? (
          <>
            <div className="tg-section-title">Answered</div>
            {answered.map((a) => (
              <div className="tg-card" key={a.id}>
                <div>{a.question}</div>
                <div className="tg-meta">
                  <b className="tg-answered">{answerLabel(a)}</b>
                  {` via ${a.answered_via === 'telegram' ? 'Telegram' : 'the app'} · ${ago(a.answered_at ?? a.created_at, now)}`}
                </div>
              </div>
            ))}
          </>
        ) : null}
      </>
    );
  }

  const sub = expired
    ? 'Not updated'
    : updated
      ? 'Updated just now. Pull down to refresh.'
      : 'Loading...';
  const tabs: Array<[Tab, string, string]> = [
    ['today', '☀', 'Today'],
    ['need', '❗', 'Need you'],
    ['log', '📜', 'Log'],
    ['team', '💬', 'Team'],
  ];

  return (
    <div className="tg-page">
      <header className="tg-header">
        <h1 className="tg-title">Kyu's AI Team</h1>
        <div className="tg-sub">{sub}</div>
      </header>
      <main
        className="tg-main"
        ref={mainRef}
        onTouchStart={(e) => {
          pullFrom.current =
            mainRef.current && mainRef.current.scrollTop > 0
              ? null
              : (e.touches[0]?.clientY ?? null);
        }}
        onTouchEnd={(e) => {
          const from = pullFrom.current;
          pullFrom.current = null;
          const to = e.changedTouches[0]?.clientY;
          if (!expired && from !== null && to !== undefined && to - from > PULL_PX) refresh();
        }}
      >
        {body()}
      </main>
      <nav className="tg-nav">
        {tabs.map(([id, icon, label]) => (
          <button
            type="button"
            key={id}
            className={`tg-tab${tab === id ? ' tg-tab-on' : ''}`}
            onClick={() => {
              setTab(id);
              setChat(null);
              mainRef.current?.scrollTo?.(0, 0);
            }}
          >
            <span className="tg-tab-icon">{icon}</span>
            {label}
            {id === 'need' && open.length > 0 ? (
              <span className="tg-badge">{open.length}</span>
            ) : null}
            {id === 'team' && unreadTotal > 0 ? (
              <span className="tg-badge">{unreadTotal}</span>
            ) : null}
          </button>
        ))}
      </nav>
    </div>
  );
}
